import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Input, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import UseApi from "../../../Reusable/CallApi/UseApi";
// import UseApi from "./api/UseApi";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f4f5f7;
  padding: 32px;
  font-family: "Poppins", "Inter", sans-serif;
`;

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #edeff2;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
  padding: 24px;
`;

const Breadcrumb = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #8492a6;
  margin-bottom: 20px;

  span {
    cursor: pointer;
  }

  .separator {
    margin: 0 8px;
    cursor: default;
  }

  .current {
    color: #1e2a3a;
    font-weight: 500;
    cursor: default;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  flex: 1;
  background: #f4f4f5;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1e2a3a;
`;

const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #8492a6;
  margin-top: 4px;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #edeff2;
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 8px 0;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: ${(props) => (props.$active ? "#1e2a3a" : "#8492a6")};
  border-bottom: 2px solid
    ${(props) => (props.$active ? "#1e2a3a" : "transparent")};
  cursor: pointer;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 16px;

  .ant-input-affix-wrapper {
    width: 260px;
    border-radius: 24px;
    border: 1px solid #e4e7eb;
    padding: 8px 16px;
  }

  .anticon {
    color: #8492a6;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #f7f8fa;
    color: #1e2a3a;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    border-bottom: none;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    font-size: 14px;
    font-weight: 400;
    color: #5a6b85;
    white-space: nowrap;
    border-bottom: 1px solid #f0f1f3;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafbfc;
  }

  .ant-table-body::-webkit-scrollbar {
    height: 6px;
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    background: #d9dce1;
    border-radius: 3px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 24px 0 8px;
`;

const PagerButton = styled.button`
  width: 36px;
  height: 32px;
  border: none;
  background: #d9dce1;
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;

  &:first-child {
    border-radius: 16px 0 0 16px;
  }

  &:last-child {
    border-radius: 0 16px 16px 0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const courseColumns = [
  { title: "Code", dataIndex: "code", key: "code" },
  { title: "Course Name", dataIndex: "courseName", key: "courseName" },
  { title: "Category", dataIndex: "category", key: "category" },
  { title: "People Enrolled", dataIndex: "enrolledIn", key: "enrolledIn" },
  { title: "Completed", dataIndex: "completed", key: "completed" },
  { title: "In Progress", dataIndex: "inProgress", key: "inProgress" },
  { title: "Not Passed", dataIndex: "notPassed", key: "notPassed" },
];

const peopleColumns = [
  { title: "Employee ID", dataIndex: "employeeId", key: "employeeId" },
  { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
  { title: "Role", dataIndex: "role", key: "role" },
  { title: "Type", dataIndex: "type", key: "type" },
  { title: "Enrolled in", dataIndex: "enrolledIn", key: "enrolledIn" },
  { title: "Completed", dataIndex: "completed", key: "completed" },
  { title: "In Progress", dataIndex: "inProgress", key: "inProgress" },
  { title: "Not Passed", dataIndex: "notPassed", key: "notPassed" },
];

const activityColumns = [
  { title: "Activity Type", dataIndex: "actionType", key: "actionType" },
  { title: "Date & Time", dataIndex: "actionDate", key: "actionDate" },
  { title: "Detail", dataIndex: "actionLog", key: "actionLog" },
];

const PAGE_SIZE = 4;

const GroupDetailsPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const clientId = useSelector(
    (state) => state.authTracking.userInfo.clientId
  );

  const groupApi = UseApi();
  const peopleApi = UseApi();
  const courseApi = UseApi();
  const detailApi = UseApi();
  const activityApi = UseApi();

  const [group, setGroup] = useState(location.state?.group || null);
  const [peopleData, setPeopleData] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [groupDetailData, setGroupDetailData] = useState([]);
  const [activityLogData, setActivityLogData] = useState([]);
  const [activeTab, setActiveTab] = useState("people");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!clientId || group) return;

    groupApi
      .request({ endPoint: `/report/getgroupreport/${clientId}`, method: "get" })
      .then((result) => {
        const match = (result?.response || []).find(
          (item) => String(item.groupId) === String(groupId)
        );
        setGroup(match || null);
      })
      .catch(() => setGroup(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, groupId]);

  useEffect(() => {
    if (!clientId) return;

    peopleApi
      .request({ endPoint: `/report/getpeoplereport/${clientId}`, method: "get" })
      .then((result) => setPeopleData(result?.response || []))
      .catch(() => setPeopleData([]));

    courseApi
      .request({ endPoint: `/report/getcoursereport/${clientId}`, method: "get" })
      .then((result) => setCourseData(result?.response || []))
      .catch(() => setCourseData([]));

    detailApi
      .request({ endPoint: `/report/getgroupdetailreport/${clientId}`, method: "get" })
      .then((result) => setGroupDetailData(result?.response || []))
      .catch(() => setGroupDetailData([]));

    activityApi
      .request({ endPoint: `/report/getactivitylog/${clientId}`, method: "get" })
      .then((result) => setActivityLogData(result?.response || []))
      .catch(() => setActivityLogData([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // getgroupdetailreport returns one row per (group, person, assigned course)
  // combo for every group client-wide - filter down to just this group, then
  // aggregate client-side into per-person and per-course rollups. Without
  // this, the People/Course tabs below used to show the same global,
  // client-wide report data on every group's detail page regardless of
  // which group was clicked.
  const groupRows = useMemo(
    () => groupDetailData.filter((row) => String(row.groupId) === String(groupId)),
    [groupDetailData, groupId]
  );

  const scopedPeopleData = useMemo(() => {
    const roleTypeById = new Map(
      peopleData.map((p) => [p.employeeId, { role: p.role, type: p.type }])
    );
    const byPerson = new Map();
    groupRows.forEach((row) => {
      if (row.employeeId == null) return;
      if (!byPerson.has(row.employeeId)) {
        byPerson.set(row.employeeId, {
          employeeId: row.employeeId,
          employeeName: row.employeeName,
          role: roleTypeById.get(row.employeeId)?.role || "-",
          type: roleTypeById.get(row.employeeId)?.type || "-",
          enrolledIn: 0,
          completed: 0,
          inProgress: 0,
          notPassed: 0,
        });
      }
      if (row.courseId == null) return;
      const entry = byPerson.get(row.employeeId);
      if (row.progress !== null && row.progress !== undefined) entry.enrolledIn += 1;
      if (row.status === "completed") entry.completed += 1;
      else if (row.status === "in_progress") entry.inProgress += 1;
      else if (row.status === "not_passed") entry.notPassed += 1;
    });
    return Array.from(byPerson.values());
  }, [groupRows, peopleData]);

  const scopedCourseData = useMemo(() => {
    const metaByCourseId = new Map(
      courseData.map((c) => [c.courseId, { code: c.code, category: c.category }])
    );
    const byCourse = new Map();
    groupRows.forEach((row) => {
      if (row.courseId == null) return;
      if (!byCourse.has(row.courseId)) {
        byCourse.set(row.courseId, {
          courseId: row.courseId,
          courseName: row.courseName,
          code: metaByCourseId.get(row.courseId)?.code ?? "-",
          category: metaByCourseId.get(row.courseId)?.category || "-",
          enrolledIn: 0,
          completed: 0,
          inProgress: 0,
          notPassed: 0,
        });
      }
      if (row.employeeId == null) return;
      const entry = byCourse.get(row.courseId);
      if (row.progress !== null && row.progress !== undefined) entry.enrolledIn += 1;
      if (row.status === "completed") entry.completed += 1;
      else if (row.status === "in_progress") entry.inProgress += 1;
      else if (row.status === "not_passed") entry.notPassed += 1;
    });
    return Array.from(byCourse.values());
  }, [groupRows, courseData]);

  const completionRate = useMemo(() => {
    if (!scopedCourseData.length) return 0;
    const totals = scopedCourseData.reduce(
      (acc, course) => ({
        enrolled: acc.enrolled + (course.enrolledIn || 0),
        completed: acc.completed + (course.completed || 0),
      }),
      { enrolled: 0, completed: 0 }
    );
    return totals.enrolled
      ? Math.round((totals.completed / totals.enrolled) * 100)
      : 0;
  }, [scopedCourseData]);

  const tableConfig = {
    people: {
      columns: peopleColumns,
      data: scopedPeopleData,
      loading: peopleApi.loading || detailApi.loading,
    },
    course: {
      columns: courseColumns,
      data: scopedCourseData,
      loading: courseApi.loading || detailApi.loading,
    },
    activity: {
      columns: activityColumns,
      data: activityLogData,
      loading: activityApi.loading,
    },
  };

  const { columns, data, loading } = tableConfig[activeTab];

  const filteredData = data.filter((row) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1;
  const pagedData = filteredData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  return (
    <PageWrapper>
      <ContentCard>
        <Breadcrumb>
          <span onClick={() => navigate("/course-report")}>Group Report</span>
          <span className="separator">&gt;</span>
          <span className="current">{group?.groupName}</span>
        </Breadcrumb>

        <StatsRow>
          <StatCard>
            <StatNumber>{group?.peopleCount ?? "-"}</StatNumber>
            <StatLabel>People</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{scopedCourseData.length}</StatNumber>
            <StatLabel>Assigned Courses</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{completionRate}%</StatNumber>
            <StatLabel>Completion Rate</StatLabel>
          </StatCard>
        </StatsRow>

        <TabsRow>
          <TabButton
            $active={activeTab === "people"}
            onClick={() => handleTabChange("people")}
          >
            People
          </TabButton>
          <TabButton
            $active={activeTab === "course"}
            onClick={() => handleTabChange("course")}
          >
            Course
          </TabButton>
          <TabButton
            $active={activeTab === "activity"}
            onClick={() => handleTabChange("activity")}
          >
            Activity Log
          </TabButton>
        </TabsRow>

        <SearchInputWrapper>
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </SearchInputWrapper>

        <StyledTable
          columns={columns}
          dataSource={pagedData}
          rowKey={(record, index) => index}
          loading={loading}
          pagination={false}
          scroll={{ x: true }}
        />

        <PaginationWrapper>
          <PagerButton
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
          >
            &#8249;
          </PagerButton>
          <PagerButton
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
          >
            &#8250;
          </PagerButton>
        </PaginationWrapper>
      </ContentCard>
    </PageWrapper>
  );
};

export default GroupDetailsPage;